import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Permission, PermissionSubject, PermissionVerb } from '@s8l/client-tree-lib';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { AccessControlModalComponent } from 'src/app/components/access-modal/access-modal.component';
import { CmsService } from 'src/app/services/cms.service';
import { AccessFilter, StatsServerService } from 'src/app/services/jsonrpc/stats-server.service';
import { Profile, Role } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-dashboard-access-control',
  templateUrl: './dashboard-access-control.component.html',
  styleUrls: ['./dashboard-access-control.component.scss']
})
export class DashboardAccessControlComponent implements OnInit, OnDestroy {
  public filters: AccessFilter[];
  public users: Profile[];
  public roles: Role[];

  private subUsers: Subscription;
  private subRoles: Subscription;

  constructor(
    private stats: StatsServerService,
    private user: UserService,
    private toastr: ToastrService,
    private modal: NgbModal,
    private cms: CmsService
  ) {}

  public async ngOnInit() {
    await this.loadFilters();

    this.subUsers = this.user.users.subscribe(res => {
      this.users = res;
      this.loadUsernames();
    });

    this.subRoles = this.user.roles.subscribe(roles => (this.roles = roles));
  }

  private loadFilters() {
    return this.stats.adminFilterList().then(filters => {
      this.filters = filters.sort((a, b) => a.name.localeCompare(b.name));
      this.loadUsernames();
    });
  }

  private loadUsernames() {
    if (this.filters == null || this.users == null) return;
    this.filters.forEach(filter => {
      if (filter?.meta?.users?.length > 0) {
        (filter.meta as any).usernames = filter.meta.users
          .map(uuid => this.users.find(u => u.uuid == uuid))
          .filter(p => p != null)
          .map(p => p.username);
      }
    });
  }

  public ngOnDestroy(): void {
    this.subUsers?.unsubscribe();
    this.subRoles?.unsubscribe();
  }

  public openCreateModal() {
    const modalRef = this.modal.open(AccessControlModalComponent, { size: 'lg' });
    modalRef.componentInstance.update = false;
    modalRef.componentInstance.roles = this.roles;
    modalRef.componentInstance.users = this.users;
    modalRef.componentInstance.onSubmit.subscribe((f: AccessFilter) => this.create(f));
  }

  public openUpdateModal(filter: AccessFilter) {
    const prev = this.copy(filter);
    const modalRef = this.modal.open(AccessControlModalComponent, { size: 'lg' });
    modalRef.componentInstance.update = true;
    modalRef.componentInstance.roles = this.roles;
    modalRef.componentInstance.users = this.users;
    modalRef.componentInstance.filter = filter;
    modalRef.componentInstance.onSubmit.subscribe((f: AccessFilter) => this.update(f, prev));
  }

  public deleteSwalOptions(filter: AccessFilter) {
    return Swal.fire({
      title: 'Delete access control rule?',
      html: `<p>Do you want to delete <b style="font-weight: bold;">${filter.name}</b>?</p><p>You won\'t be able to revert this!</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-primary me-1',
        cancelButton: 'btn btn-danger'
      }
    }).then(result => {
      if (result.isConfirmed) {
        return this.delete(filter);
      }
    });
  }

  private allRoles(filter: AccessFilter): string[] {
    const roles: string[] = [];
    filter.meta?.roles?.forEach(role => roles.push(role));
    filter.meta?.users?.forEach(uuid => roles.push('usr#' + uuid));
    return roles;
  }

  public create(filter: AccessFilter) {
    return this.stats
      .adminFilterCreate(filter)
      .then(f => {
        const permission = new Permission(PermissionSubject.Stats, PermissionVerb.Read);
        return Promise.all(this.allRoles(f).map(role => this.cms.pushAccessVerb(role, true, filter.uuid, [permission])));
      })
      .then(() => this.toastr.success(`Created access control rule "${filter.name}"`))
      .then(() => this.loadFilters());
  }

  public update(filter: AccessFilter, previous: AccessFilter) {
    if (filter.uuid !== previous.uuid) return;
    const prvRoles = this.allRoles(previous);
    const newRoles = this.allRoles(filter);
    const deleteRoles = prvRoles.filter(r => !newRoles.includes(r));
    const createRoles = newRoles.filter(r => !prvRoles.includes(r));
    return this.stats
      .adminFilterUpdate(filter)
      .then(() => {
        const permission = new Permission(PermissionSubject.Stats, PermissionVerb.Read);
        const promises: Promise<any>[] = [
          ...createRoles.map(role => this.cms.pushAccessVerb(role, true, filter.uuid, [permission])),
          ...deleteRoles.map(role => this.cms.pushAccessVerb(role, false, filter.uuid, [permission]))
        ];
        return Promise.all(promises);
      })
      .then(() => this.toastr.success(`Updated access control rule "${filter.name}"`))
      .then(() => this.loadFilters());
  }

  public delete(filter: AccessFilter) {
    return this.stats
      .adminFilterDelete(filter.uuid)
      .then(() => {
        const permission = new Permission(PermissionSubject.Stats, PermissionVerb.Read);
        return Promise.all(this.allRoles(filter).map(role => this.cms.pushAccessVerb(role, false, filter.uuid, [permission])));
      })
      .then(() => this.toastr.success(`Deleted access control rule "${filter.name}"`))
      .then(() => this.loadFilters());
  }

  private copy(item) {
    return JSON.parse(JSON.stringify(item));
  }
}
